import React, { useState, useEffect } from "react";
import { Table, Tag, Button, Space, Alert, Spin, Tooltip, Modal } from "antd";
import { ReloadOutlined, UserOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { getEmailFullStatus } from "../../api/mailApi";
import dayjs from "dayjs";

/**
 * 群发邮件收件人状态查看器
 * 显示每个收件人的发送状态详情
 */
const BulkEmailRecipientsViewer = ({ visible, onClose, mainEmailId, title = "群发邮件收件人状态" }) => {
    const [loading, setLoading] = useState(false);
    const [recipientsData, setRecipientsData] = useState(null);
    const [error, setError] = useState(null);

    // 加载收件人状态数据
    const loadRecipientsStatus = async () => {
        if (!mainEmailId) return;

        try {
            setLoading(true);
            setError(null);

            const response = await getEmailFullStatus(mainEmailId, true);

            if (response?.data) {
                const adaptedData = {
                    summary: response.data.bulk_status?.summary || {},
                    detailed_status: response.data.bulk_status?.recipients || [],
                };

                setRecipientsData(adaptedData);
            } else {
                setError("获取收件人状态失败：响应数据格式错误");
            }
        } catch (err) {
            console.error("获取群发邮件收件人状态失败:", err);
            setError(`获取收件人状态失败: ${err.message || "未知错误"}`);
        } finally {
            setLoading(false);
        }
    };

    // 当模态框打开时加载数据
    useEffect(() => {
        if (visible && mainEmailId) {
            loadRecipientsStatus();
        }
    }, [visible, mainEmailId]);

    // 状态映射
    const STATUS_CONFIG = {
        PENDING: { color: "default", icon: <ClockCircleOutlined />, text: "等待发送" },
        SENDING: { color: "processing", icon: <ClockCircleOutlined />, text: "发送中" },
        SENT: { color: "success", icon: <CheckCircleOutlined />, text: "发送成功" },
        FAILED: { color: "error", icon: <CloseCircleOutlined />, text: "发送失败" },
        SKIPPED: { color: "warning", icon: <CloseCircleOutlined />, text: "跳过发送" },
    };

    // 表格列定义
    const columns = [
        {
            title: "序号",
            dataIndex: "index",
            key: "index",
            width: 60,
            render: (_, __, index) => index + 1,
        },
        {
            title: "收件人邮箱",
            dataIndex: "email",
            key: "email",
            ellipsis: true,
            render: (email) => (
                <Space>
                    <UserOutlined style={{ color: "#1890ff" }} />
                    <span>{email}</span>
                </Space>
            ),
        },
        {
            title: "收件人类型",
            dataIndex: "type",
            key: "type",
            width: 100,
            render: (type) => <Tag color={type === "TO" ? "blue" : type === "CC" ? "cyan" : type === "BCC" ? "purple" : "default"}>{type}</Tag>,
        },
        {
            title: "发送状态",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (status) => {
                const config = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
                return (
                    <Tag color={config.color} icon={config.icon}>
                        {config.text}
                    </Tag>
                );
            },
        },
        {
            title: "发送时间",
            dataIndex: "sent_at",
            key: "sent_at",
            width: 150,
            render: (sentAt) => (sentAt ? dayjs(sentAt).format("MM-DD HH:mm:ss") : "-"),
        },
        {
            title: "错误信息",
            dataIndex: "error_message",
            key: "error_message",
            ellipsis: true,
            render: (error) =>
                error ? (
                    <Tooltip title={error}>
                        <Tag color="red" style={{ cursor: "pointer" }}>
                            查看错误
                        </Tag>
                    </Tooltip>
                ) : (
                    "-"
                ),
        },
    ];

    // 渲染内容
    const renderContent = () => {
        if (loading) {
            return (
                <div style={{ textAlign: "center", padding: "40px" }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}>加载收件人状态中...</div>
                </div>
            );
        }

        if (error) {
            return (
                <Alert
                    message="加载失败"
                    description={error}
                    type="error"
                    showIcon
                    action={
                        <Button size="small" onClick={loadRecipientsStatus}>
                            重试
                        </Button>
                    }
                />
            );
        }

        if (!recipientsData) {
            return <Alert message="暂无数据" type="info" showIcon />;
        }

        const { summary } = recipientsData;
        const recipients = summary?.status_details || recipientsData.detailed_status || [];

        return (
            <div>
                {/* 统计信息 */}
                <div style={{ marginBottom: 16, padding: "12px", backgroundColor: "#f5f5f5", borderRadius: "6px" }}>
                    <div style={{ marginBottom: 8 }}>
                        <strong>发送统计</strong>
                    </div>
                    <Space>
                        <Tag color="blue">总数: {summary.total_recipients || summary.total_count || 0}</Tag>
                        <Tag color="default">等待: {summary.total_pending || summary.pending_count || 0}</Tag>
                        <Tag color="processing">发送中: {summary.total_sending || summary.sending_count || 0}</Tag>
                        <Tag color="green">成功: {summary.total_sent || summary.sent_count || 0}</Tag>
                        <Tag color="red">失败: {summary.total_failed || summary.failed_count || 0}</Tag>
                        <Tag color="orange">跳过: {summary.total_skipped || summary.skipped_count || 0}</Tag>
                    </Space>
                    <div style={{ marginTop: 8 }}>
                        <Tag color={summary.overall_status === "ALL_SENT" ? "green" : summary.overall_status === "PARTIAL_SENT" ? "orange" : summary.overall_status === "ALL_FAILED" ? "red" : summary.overall_status === "SENDING" ? "processing" : "default"}>整体状态: {summary.overall_status || "UNKNOWN"}</Tag>
                    </div>
                </div>

                {/* 收件人详细状态表格 */}
                <Table
                    columns={columns}
                    dataSource={recipients}
                    rowKey="id"
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
                    }}
                    size="small"
                    scroll={{ x: 800 }}
                />
            </div>
        );
    };

    return (
        <Modal
            title={
                <Space>
                    <span>{title}</span>
                    <Button type="text" size="small" icon={<ReloadOutlined />} onClick={loadRecipientsStatus} loading={loading}>
                        刷新
                    </Button>
                </Space>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={1000}
            destroyOnHidden
            style={{ top: 20 }}
        >
            {renderContent()}
        </Modal>
    );
};

export default BulkEmailRecipientsViewer;
